import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconAlertTriangle, IconAlertCircle, IconInfoCircle, IconX } from '@tabler/icons-react';
import { ToastItem, ToastType, dismissToast } from '../../services/toastStore';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

// Same success/warning/error tones as SettingsPillBadge, and the teal
// accent already used for Info elsewhere (filterSliderTheme) -- reusing
// both instead of picking new colors keeps every "status" surface in the
// app (badges, sliders, toasts) reading as one consistent language.
const TOAST_COLORS: Record<ToastType, { bg: string; fg: string }> = {
  success: { bg: '#E6F4EA', fg: '#2E7D42' },
  warning: { bg: '#FFF1E6', fg: '#C9762F' },
  error: { bg: '#FDEAE6', fg: '#C24A3B' },
  info: { bg: '#E7F5EF', fg: '#1D9E75' },
};

const TOAST_ICONS: Record<ToastType, React.ElementType> = {
  success: IconCheck,
  warning: IconAlertTriangle,
  error: IconAlertCircle,
  info: IconInfoCircle,
};

interface ToastCloseButtonProps {
  onClick: () => void;
}

const ToastCloseButton: React.FC<ToastCloseButtonProps> = ({ onClick }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [hovering, setHovering] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      aria-label={translate(t, TRANSLATIONS.TOAST_DISMISS_LABEL)}
      style={{
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        // 44x44 touch target via padding, not a bigger icon -- the negative
        // top/right margins grew by the same amount the padding did, so the
        // icon itself stays tucked in the same spot near the card's corner
        // instead of visually drifting inward as the button grew.
        padding: 14,
        flexShrink: 0,
        marginTop: -12,
        marginRight: -16,
        display: 'flex',
        color: hovering ? token.colorText : token.colorTextTertiary,
        transition: 'color 150ms ease',
      }}
    >
      <IconX size={16} />
    </button>
  );
};

interface ToastProps {
  toast: ToastItem;
}

/**
 * Single toast card -- the app's unified replacement for antd's own
 * `message` popups (see toastMessage.ts for the drop-in-compatible API
 * every call site still uses). Icon circle + text on top, a thin progress
 * bar along the bottom that empties in real time until auto-dismiss.
 *
 * The countdown is a plain rAF loop instead of a single setTimeout so
 * hovering can pause it mid-flight: `remainingRef` holds how much time is
 * left, and each pause/resume just starts a fresh "segment" against it
 * (see the effect below) rather than restarting the whole countdown.
 */
const Toast: React.FC<ToastProps> = ({ toast }) => {
  const { token } = theme.useToken();
  const { bg, fg } = TOAST_COLORS[toast.type];
  const Icon = TOAST_ICONS[toast.type];
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(1);
  const remainingRef = useRef(toast.duration);
  const segmentStartRef = useRef(Date.now());

  useEffect(() => {
    if (paused) return;
    segmentStartRef.current = Date.now();
    let frameId: number;
    const tick = () => {
      const elapsedThisSegment = Date.now() - segmentStartRef.current;
      const totalElapsed = toast.duration - remainingRef.current + elapsedThisSegment;
      const pct = Math.max(0, 1 - totalElapsed / toast.duration);
      setProgress(pct);
      if (pct <= 0) {
        dismissToast(toast.id);
        return;
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - segmentStartRef.current));
    };
  }, [paused, toast.duration, toast.id]);

  const isPlainText = typeof toast.content === 'string';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -16, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: 'relative',
        width: 380,
        maxWidth: 'calc(100vw - 32px)',
        background: token.colorBgContainer,
        borderRadius: 16,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px' }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={18} color={fg} />
        </div>
        <div style={{ flex: 1, minWidth: 0, paddingTop: 1, color: token.colorText }}>
          {isPlainText ? (
            <span style={{ fontSize: 14, fontWeight: 600, display: 'block' }}>{toast.content}</span>
          ) : (
            <div style={{ fontSize: 14 }}>{toast.content}</div>
          )}
        </div>
        <ToastCloseButton onClick={() => dismissToast(toast.id)} />
      </div>
      <div style={{ height: 3, background: token.colorFillTertiary }}>
        <div style={{ height: '100%', width: `${progress * 100}%`, background: fg, borderRadius: 999 }} />
      </div>
    </motion.div>
  );
};

export default Toast;
